import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { signShopOrdersApi, SignOrder } from '../api/signShopOrders';
import { useAuth } from '../hooks/useAuth';
import { isAdmin } from '../utils/roles';

const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const statusMeta: Partial<Record<SignOrder['status'], { label: string; color: string }>> = {
  pending_payment:        { label: 'Awaiting payment',  color: 'yellow' },
  paid_pending_approval:  { label: 'Paid — needs approval', color: 'blue' },
  approved:               { label: 'Approved — sent to production', color: 'green' },
  approval_failed:        { label: 'Approval failed', color: 'red' },
  rejected:               { label: 'Rejected', color: 'gray' },
  in_production:          { label: 'In production', color: 'blue' },
  shipped:                { label: 'Shipped', color: 'green' },
  delivered:              { label: 'Delivered', color: 'green' },
  refunded:               { label: 'Refunded', color: 'gray' },
  canceled:               { label: 'Canceled', color: 'gray' },
};

const STATUS_FILTERS: Array<{ key: string; label: string }> = [
  { key: 'all',                    label: 'All' },
  { key: 'paid_pending_approval',  label: 'Needs approval' },
  { key: 'approved',               label: 'Approved' },
  { key: 'approval_failed',        label: 'Failed' },
  { key: 'rejected',               label: 'Rejected' },
  { key: 'pending_payment',        label: 'Unpaid' },
];

export const SignShopOrders: React.FC = () => {
  const { user } = useAuth();
  const customerView = !user || !isAdmin(user.role);
  const [orders, setOrders] = useState<SignOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('paid_pending_approval');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<SignOrder | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await signShopOrdersApi.listOrders();
      setOrders(list);
    } catch (e: any) {
      setError(e?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      let syncError: string | null = null;
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get('session_id') || params.get('paid_session');
      if (sessionId) {
        try {
          await signShopOrdersApi.syncSession(sessionId);
          const url = new URL(window.location.href);
          url.searchParams.delete('session_id');
          url.searchParams.delete('paid_session');
          window.history.replaceState(null, '', url.toString());
        } catch (e: any) {
          syncError = `Payment received, but order sync failed: ${e?.message || e}`;
        }
      }
      await load();
      if (syncError) setError(syncError);
    })();
  }, []);

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const approve = async (o: SignOrder) => {
    if (!window.confirm(`Approve order ${o.id.slice(0, 8)}… and submit ${o.cart.length} item(s) for production?`)) return;
    const notes = window.prompt('Approval notes (required):', 'Artwork, QR target, quantity, and shipping address verified');
    if (!notes?.trim()) return;
    const artworkChecksums = Array.from(new Set(o.cart.map(line => line.production_checksum).filter((value): value is string => !!value)));
    if (artworkChecksums.length === 0) {
      setError('This order has no immutable artwork checksum and cannot be approved safely.');
      return;
    }
    setPendingId(o.id);
    try {
      const updated = await signShopOrdersApi.approve(o.id, notes, artworkChecksums);
      setOrders(prev => prev.map(x => x.id === o.id ? updated : x));
    } catch (e: any) {
      setError(`Approve failed: ${e?.message || e}`);
      await load();
    } finally {
      setPendingId(null);
    }
  };

  const reject = async () => {
    if (!rejecting) return;
    setPendingId(rejecting.id);
    try {
      const updated = await signShopOrdersApi.reject(rejecting.id, rejectReason);
      setOrders(prev => prev.map(x => x.id === rejecting.id ? updated : x));
      setRejecting(null);
      setRejectReason('');
    } catch (e: any) {
      setError(`Reject failed: ${e?.message || e}`);
    } finally {
      setPendingId(null);
    }
  };

  const filtered = filter === 'all' ? orders : orders.filter(o => o.status === filter);

  if (customerView) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6">
        <div className="shadow-page-frame max-w-7xl mx-auto mb-6 flex items-center justify-between gap-3 flex-wrap">
          <div><h1 className="text-3xl font-bold text-gray-900">My Sign Orders</h1><p className="text-gray-500 mt-1">View your order, invoice, production status, and shipment tracking.</p></div>
          <Button onClick={load} variant="secondary" size="sm" loading={loading}>Refresh</Button>
        </div>
        {error && <div className="max-w-7xl mx-auto mb-4"><Card><div className="text-red-700 text-sm">{error}</div></Card></div>}
        <div className="max-w-7xl mx-auto space-y-3">
          {loading && <Card><div className="text-gray-500 text-center py-4">Loading…</div></Card>}
          {!loading && orders.length === 0 && <Card><div className="text-gray-500 text-center py-8">You have no Sign Shop orders yet.</div></Card>}
          {!loading && orders.map(o => {
            const customerStatus = o.status === 'paid_pending_approval' ? 'Payment received — preparing order'
              : o.status === 'approved' ? 'Order approved'
              : (statusMeta[o.status]?.label || o.status);
            return <Card key={o.id} padding={false} className="overflow-hidden"><div className="p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div><div className="flex items-center gap-2"><Badge color={statusMeta[o.status]?.color || 'gray'}>{customerStatus}</Badge><span className="text-xs text-gray-500">Order {o.id.slice(0, 8).toUpperCase()}</span></div><div className="mt-2 text-sm text-gray-500">Placed {new Date(o.created_at).toLocaleString()}</div></div>
                <div className="text-right"><div className="text-xl font-semibold">{fmt(o.total_cents)}</div><div className="text-xs text-gray-500">{o.cart.length} item{o.cart.length === 1 ? '' : 's'}</div></div>
              </div>
              <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-4">
                <div><div className="text-xs font-semibold uppercase text-gray-500 mb-1">Invoice</div>{o.hosted_invoice_url || o.invoice_pdf ? <div className="flex gap-3 text-sm">{o.hosted_invoice_url && <a className="text-emerald-700 hover:underline" href={o.hosted_invoice_url} target="_blank" rel="noreferrer">View invoice</a>}{o.invoice_pdf && <a className="text-emerald-700 hover:underline" href={o.invoice_pdf} target="_blank" rel="noreferrer">Download PDF</a>}</div> : <div className="text-sm text-gray-600">{o.stripe_payment_status === 'paid' ? 'Invoice is being prepared' : 'Available after payment'}</div>}</div>
                <div><div className="text-xs font-semibold uppercase text-gray-500 mb-1">Shipping</div><div className="text-sm text-gray-700">{o.tracking?.number ? <>{o.tracking.carrier || 'Carrier'}: {o.tracking.url ? <a className="text-emerald-700 hover:underline" href={o.tracking.url} target="_blank" rel="noreferrer">{o.tracking.number}</a> : o.tracking.number}</> : 'Tracking will appear here when shipped'}</div></div>
                <div><div className="text-xs font-semibold uppercase text-gray-500 mb-1">Ship to</div><div className="text-sm text-gray-700">{o.address.address1}{o.address.address2 ? `, ${o.address.address2}` : ''}<br />{o.address.city}, {o.address.region} {o.address.zip}</div></div>
              </div>
              <div className="mt-4 divide-y border rounded">{o.cart.map((l, i) => <div key={i} className="p-3 flex items-center gap-3 text-sm">{l.imageSrc && <img src={l.imageSrc} alt="" className="w-12 h-12 object-cover rounded" />}<div className="flex-1"><div className="font-medium">{l.productTitle}</div><div className="text-gray-500">{l.variantTitle} × {l.quantity}</div></div><div>{fmt((l.unit_price_cents ?? l.unitPrice ?? 0) * l.quantity)}</div></div>)}</div>
            </div></Card>;
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto mb-6 flex items-center justify-between gap-3 flex-wrap">
    <div className="shadow-page-frame legacy-order-page">
          <h1 className="text-3xl font-bold text-gray-900">Sign Shop Orders</h1>
          <p className="text-gray-500 mt-1">Review paid orders and approve them for production.</p>
        </div>
        <Button onClick={load} variant="secondary" size="sm" loading={loading}>Refresh</Button>
      </div>

      <div className="max-w-7xl mx-auto mb-4 flex flex-wrap gap-2">
        {STATUS_FILTERS.map(f => {
          const count = f.key === 'all' ? orders.length : orders.filter(o => o.status === f.key).length;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                filter === f.key
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-400'
              }`}
            >
              {f.label}{count > 0 ? ` (${count})` : ''}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="max-w-7xl mx-auto mb-4">
          <Card><div className="text-red-700 text-sm">{error}</div></Card>
        </div>
      )}

      <div className="max-w-7xl mx-auto space-y-3">
        {loading && (
          <Card><div className="text-gray-500 text-center py-4">Loading…</div></Card>
        )}
        {!loading && filtered.length === 0 && (
          <Card><div className="text-gray-500 text-center py-4">No orders in this view.</div></Card>
        )}
        {!loading && filtered.map(o => {
          const meta = statusMeta[o.status] || { label: o.status, color: 'gray' };
          const isOpen = expanded.has(o.id);
          return (
            <Card key={o.id} padding={false} className="overflow-hidden">
              <div className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge color={meta.color}>{meta.label}</Badge>
                      <code className="text-xs text-gray-500">{o.id.slice(0, 8)}…</code>
                      <span className="text-xs text-gray-400">{new Date(o.created_at).toLocaleString()}</span>
                    </div>
                    <div className="mt-1 font-medium text-gray-900">
                      {o.customer_email} — {o.cart.length} item{o.cart.length === 1 ? '' : 's'} — {fmt(o.total_cents)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {o.address.first_name} {o.address.last_name}, {o.address.address1}, {o.address.city} {o.address.region} {o.address.zip}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button onClick={() => toggleExpand(o.id)} size="sm" variant="ghost">
                      {isOpen ? 'Hide' : 'Details'}
                    </Button>
                    {o.status === 'paid_pending_approval' && (
                      <>
                        <Button onClick={() => approve(o)} size="sm" loading={pendingId === o.id}>
                          Approve & submit
                        </Button>
                        <Button onClick={() => setRejecting(o)} size="sm" variant="secondary">
                          Reject
                        </Button>
                      </>
                    )}
                    {o.status === 'approval_failed' && (
                      <Button onClick={() => approve(o)} size="sm" loading={pendingId === o.id}>
                        Retry approval
                      </Button>
                    )}
                    {o.printify_order_id && (
                      <span className="px-3 py-1.5 text-xs text-gray-500 self-center">
                        Production ref: <code>{o.printify_order_id.slice(0, 8)}…</code>
                      </span>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className="mt-4 border-t pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs font-semibold text-gray-600 uppercase mb-2">Cart</div>
                      <div className="divide-y border rounded">
                        {o.cart.map((l, i) => (
                          <div key={i} className="p-2 flex items-center gap-2 text-sm">
                            {l.imageSrc && <img src={l.imageSrc} alt="" className="w-10 h-10 object-cover rounded" />}
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 truncate">{l.productTitle}</div>
                              <div className="text-gray-500 text-xs">{l.variantTitle} × {l.quantity}</div>
                              <div className="text-gray-500 text-xs">
                                → {l.propertyLabel || '?'} / {l.intercomLabel || '?'}
                              </div>
                            </div>
                            <div className="text-gray-900 font-medium">{fmt(((l.unit_price_cents ?? l.unitPrice ?? 0)) * l.quantity)}</div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 text-sm space-y-1">
                        <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{fmt(o.subtotal_cents)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Shipping</span><span>{fmt(o.shipping_cents)}</span></div>
                        <div className="flex justify-between font-semibold"><span>Total</span><span>{fmt(o.total_cents)}</span></div>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-600 uppercase mb-2">Status & references</div>
                      <dl className="text-sm space-y-1">
                        <div className="flex gap-2"><dt className="text-gray-500 w-32">Stripe session</dt><dd className="text-gray-800 truncate">{o.stripe_session_id || '—'}</dd></div>
                        <div className="flex gap-2"><dt className="text-gray-500 w-32">Payment intent</dt><dd className="text-gray-800 truncate">{o.stripe_payment_intent_id || '—'}</dd></div>
                        <div className="flex gap-2"><dt className="text-gray-500 w-32">Payment status</dt><dd className="text-gray-800">{o.stripe_payment_status || '—'}</dd></div>
                        <div className="flex gap-2"><dt className="text-gray-500 w-32">Paid at</dt><dd className="text-gray-800">{o.paid_at ? new Date(o.paid_at).toLocaleString() : '—'}</dd></div>
                        <div className="flex gap-2"><dt className="text-gray-500 w-32">Production ref</dt><dd className="text-gray-800">{o.printify_order_id || '—'}</dd></div>
                        <div className="flex gap-2"><dt className="text-gray-500 w-32">Approved at</dt><dd className="text-gray-800">{o.approved_at ? new Date(o.approved_at).toLocaleString() : '—'}</dd></div>
                        {o.printify_error && (
                          <div className="flex gap-2"><dt className="text-gray-500 w-32">Production error</dt><dd className="text-red-700 break-all">{o.printify_error}</dd></div>
                        )}
                        {o.rejection_reason && (
                          <div className="flex gap-2"><dt className="text-gray-500 w-32">Reject reason</dt><dd className="text-gray-700">{o.rejection_reason}</dd></div>
                        )}
                      </dl>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {rejecting && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setRejecting(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900 mb-2">Reject order</h2>
            <p className="text-sm text-gray-600 mb-3">
              Marks the order rejected (production will NOT be notified). Refund the charge in the Stripe dashboard separately.
            </p>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Reason (visible internally)"
              rows={3}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
            <div className="mt-4 flex justify-end gap-2">
              <Button onClick={() => setRejecting(null)} variant="secondary">Cancel</Button>
              <Button onClick={reject} variant="danger" loading={pendingId === rejecting.id}>Reject order</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
